import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEOService } from '../services/seoService';

/**
 * Hook personnalisé pour la gestion du SEO
 */
export const useSEO = (pageData = {}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [seoData, setSeoData] = useState({});

  // Mémorisation des données de page pour éviter les boucles infinies
  const memoizedPageData = useMemo(() => pageData, [
    pageData.title,
    pageData.description,
    pageData.keywords,
    pageData.type,
    pageData.author,
    pageData.canonical,
    pageData.noindex,
    pageData.nofollow,
    pageData.content,
    pageData.image,
    JSON.stringify(pageData.structuredData),
    JSON.stringify(pageData.breadcrumbs)
  ]);
  useEffect(() => {
    const generateSEOData = () => {
      const currentLang = i18n.language || 'fr';
      const baseUrl = process.env.REACT_APP_BASE_URL || 'https://www.flowdino.com';
      const siteName = t('seo.siteName', 'FlowDino');

      // Génération automatique basée sur la route
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const routeData = getRouteData(pathSegments, t);

      const seoConfig = {
        title: memoizedPageData.title || routeData.title,
        description: memoizedPageData.description || routeData.description,
        keywords: memoizedPageData.keywords || routeData.keywords,
        image: memoizedPageData.image || `${baseUrl}/images/og-flowdino-1200x630.jpg`,
        url: location.pathname,
        type: memoizedPageData.type || routeData.type || 'website',
        author: memoizedPageData.author || siteName,
        canonical: memoizedPageData.canonical,
        noindex: memoizedPageData.noindex || false,
        nofollow: memoizedPageData.nofollow || false,
        structuredData: memoizedPageData.structuredData || generateDefaultStructuredData(routeData, baseUrl),
        breadcrumbs: memoizedPageData.breadcrumbs || generateBreadcrumbs(pathSegments, t),
        alternateLanguages: SEOService.generateAlternateLanguages(location.pathname)
      };

      // Optimisation automatique
      if (seoConfig.title && !memoizedPageData.title) {
        seoConfig.title = SEOService.generateMetaTitle(seoConfig.title, routeData.category, siteName);
      }

      if (memoizedPageData.content && !memoizedPageData.description) {
        seoConfig.description = SEOService.generateMetaDescription(memoizedPageData.content);
      }

      if (memoizedPageData.content && !memoizedPageData.keywords) {
        seoConfig.keywords = SEOService.generateKeywords(memoizedPageData.content, routeData.keywords?.split(', ') || []);
      }

      setSeoData(seoConfig);
    };

    generateSEOData();
  }, [location.pathname, memoizedPageData, t, i18n.language]);

  // Validation SEO
  const validateSEO = () => {
    return SEOService.validateSEO(seoData);
  };

  return { seoData, validateSEO };
};

/**
 * Génère les données SEO basées sur la route
 */
const getRouteData = (pathSegments, t) => {
  const routes = {
    '': {
      title: t('seo.pages.home.title', 'Accueil'),
      description: t('seo.pages.home.description', 'Solution complète pour la gestion et le partage d\'articles automatisé sur différents sites Vinted, Leboncoin. Optimisez votre workflow de publication.'),
      keywords: t('seo.pages.home.keywords', 'FlowDino, gestion articles, partage contenu, workflow'),
      type: 'website'
    },
    'articles': {
      title: t('seo.pages.articles.title', 'Articles'),
      description: t('seo.pages.articles.description', 'Découvrez tous les articles disponibles sur FlowDino.'),
      keywords: t('seo.pages.articles.keywords', 'articles, blog, contenu, publication'),
      type: 'website',
      category: 'Articles'
    },
    'pricing': {
      title: t('seo.pages.pricing.title', 'Tarifs'),
      description: t('seo.pages.pricing.description', 'Découvrez nos plans tarifaires adaptés à vos besoins.'),
      keywords: t('seo.pages.pricing.keywords', 'tarifs, prix, abonnement, plans'),
      type: 'website'
    },
    'documentation': {
      title: t('seo.pages.documentation.title', 'Documentation'),
      description: t('seo.pages.documentation.description', 'Guide complet d\'utilisation de FlowDino.'),
      keywords: t('seo.pages.documentation.keywords', 'documentation, guide, aide, tutoriel'),
      type: 'website'
    },
    'dashboard': {
      title: t('seo.pages.dashboard.title', 'Tableau de bord'),
      description: t('seo.pages.dashboard.description', 'Gérez vos articles et votre compte FlowDino.'),
      keywords: t('seo.pages.dashboard.keywords', 'dashboard, gestion, compte, articles'),
      type: 'website',
      noindex: true // Page privée
    },
    'profile': {
      title: t('seo.pages.profile.title', 'Profil'),
      description: t('seo.pages.profile.description', 'Gérez votre profil et vos paramètres.'),
      keywords: t('seo.pages.profile.keywords', 'profil, paramètres, compte'),
      type: 'website',
      noindex: true // Page privée
    },
    'legal': {
      'cgu': {
        title: t('seo.pages.legal.cgu.title', 'Conditions Générales d\'Utilisation'),
        description: t('seo.pages.legal.cgu.description', 'Conditions d\'utilisation de FlowDino.'),
        keywords: t('seo.pages.legal.cgu.keywords', 'CGU, conditions, utilisation, légal')
      },
      'privacy': {
        title: t('seo.pages.legal.privacy.title', 'Politique de Confidentialité'),
        description: t('seo.pages.legal.privacy.description', 'Notre politique de protection des données.'),
        keywords: t('seo.pages.legal.privacy.keywords', 'confidentialité, données, RGPD, privacy')
      },
      'legal-notice': {
        title: t('seo.pages.legal.notice.title', 'Mentions Légales'),
        description: t('seo.pages.legal.notice.description', 'Mentions légales de FlowDino.'),
        keywords: t('seo.pages.legal.notice.keywords', 'mentions légales, éditeur, responsable')
      }
    }
  };

  // Navigation dans l'arbre des routes
  let currentRoute = routes;
  for (const segment of pathSegments) {
    if (currentRoute[segment]) {
      currentRoute = currentRoute[segment];
    } else {
      // Route non trouvée, retourner des valeurs par défaut
      return {
        title: t('seo.default.title', 'FlowDino'),
        description: t('seo.default.description', 'FlowDino - Solution complète pour la gestion d\'articles.'),
        keywords: t('seo.default.keywords', 'FlowDino, articles, gestion'),
        type: 'website'
      };
    }
  }

  return currentRoute;
};

/**
 * Génère les données structurées par défaut
 */
const generateDefaultStructuredData = (routeData, baseUrl) => {
  return SEOService.generateStructuredData('WebPage', {
    title: routeData.title,
    description: routeData.description,
    url: `${baseUrl}${window.location.pathname}`
  });
};

/**
 * Génère le fil d'Ariane
 */
const generateBreadcrumbs = (pathSegments, t) => {
  const breadcrumbs = [
    { name: t('breadcrumb.home', 'Accueil'), url: '/' }
  ];

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const name = t(`breadcrumb.${segment}`, segment.charAt(0).toUpperCase() + segment.slice(1));
    breadcrumbs.push({
      name,
      url: currentPath
    });
  });

  return breadcrumbs;
};

export default useSEO;
